import { PermissionResponse, PermissionStatus } from "expo-camera";

type PermissionChangeHandler = (isPermitted?: boolean) => void;

export abstract class ManagedPermission {
  private onChangeListeners: PermissionChangeHandler[] = [];

  constructor(public readonly token: string) {}

  protected permitted: boolean;

  get isPermitted() {
    return this.permitted;
  }

  protected abstract getPermissions(): Promise<PermissionResponse>;
  protected abstract requestModulePermission(): Promise<PermissionResponse>;

  async getPermissionResponse() {
    const response = await this.getPermissions();

    this.permitted = this.isGranted(response);

    return response;
  }

  async canAskAgain() {
    const permissions = await this.getPermissions();
    return permissions.canAskAgain;
  }

  async requestPermission() {
    if (!this.canAskAgain()) {
      throw Error(`Permission ${this.token} cannot be requested again`);
    }
    const permission = await this.requestModulePermission();
    this.permitted = this.isGranted(permission);
    this.emitChangeEvent(this.permitted);
    return permission;
  }

  addOnChangeListener(listener: PermissionChangeHandler) {
    this.onChangeListeners.push(listener);
    return () => {
      this.removeOnChangeListener(listener);
    };
  }

  removeOnChangeListener(listener: PermissionChangeHandler) {
    const index = this.onChangeListeners.indexOf(listener);
    if (index !== -1) {
      this.onChangeListeners.splice(index, 1);
    }
  }

  private emitChangeEvent(isPermitted: boolean) {
    if (this.onChangeListeners) {
      this.onChangeListeners.forEach((listener) => {
        listener(isPermitted);
      });
    }
  }

  protected isGranted(permission: PermissionResponse) {
    return permission.status === PermissionStatus.GRANTED;
  }
}
